import { IPassportGroup, IPassportSchemaField } from '@/pages/MDM/mdm.types.ts'
import { forwardRef, memo, useMemo, useRef } from 'react'
import objectUtils from '@/utils/object.utils.ts'
import { IEquipmentPassport } from '@/pages/Equipment/equipment.types.ts'
import usePassportSchema from '@/pages/MDM/hooks/usePassprotSchema.ts'
import KeyValueTable from '@/shared/Tables/KeyValueTable'
import mdmUtils from '@/pages/MDM/mdm.utils.ts'
import useUnitInstances from '@/pages/MDM/hooks/useUnitInstances.ts'
import usePrimitives from '@/pages/MDM/hooks/usePrimitives.ts'
import useActiveObservableElement from '@/shared/hooks/useActiveObservableElement'

interface IProps {
  passportInstance: IEquipmentPassport
  onGroupIntoViewportChange?: (activeGroupId: IPassportGroup['id']) => void
}

const EquipmentPassportInstanceValuesTable = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const passportSchema = usePassportSchema(props.passportInstance.passportId)
  const unitInstances = useUnitInstances()
  const primitives = usePrimitives()

  const groupedFields: Array<{ id: IPassportGroup['id']; groupName: string; children: IPassportSchemaField[] }> =
    useMemo(() => {
      if (!passportSchema) return []

      const fieldsByGroup = objectUtils.arrToKeyArray(passportSchema.fields, 'groupId')
      const result: Array<{ id: IPassportGroup['id']; groupName: string; children: IPassportSchemaField[] }> = []
      for (const group of passportSchema.groups) {
        if (fieldsByGroup[group.id]?.length) {
          result.push({
            id: group.id,
            groupName: group.name,
            children: [...fieldsByGroup[group.id]].sort((a, b) => a.orderingIndex - b.orderingIndex),
          })
        }
      }

      return result
    }, [passportSchema])

  const observingElements = useRef<{ element: HTMLElement; key: number }[]>(new Array(groupedFields.length).fill(null))

  useActiveObservableElement({
    elements: observingElements.current,
    onCurrentObservingElementChange: props.onGroupIntoViewportChange,
    active: Boolean(props.onGroupIntoViewportChange),
  })

  const renderValues = () => {
    return groupedFields.map((f, index) => (
      <div
        className="flex-lines gap16"
        ref={(el) => (observingElements.current[index] = { element: el, key: f.id })}
        key={f.id}
        id={`groupId_${f.id}`}
      >
        <h5 className="text-heading-5">{f.groupName}</h5>
        <KeyValueTable
          values={f.children.map((field) => ({
            key: field.id,
            title: field.name,
            value: mdmUtils.getFieldValue(
              primitives,
              unitInstances,
              props.passportInstance.values.find((v) => v.fieldId === field.id)
            ),
          }))}
        />
      </div>
    ))
  }

  return (
    <div ref={ref} className="flex-lines gap8 height100 width100" style={{ minHeight: 0, overflow: 'auto' }}>
      {renderValues()}
    </div>
  )
})

export default memo(EquipmentPassportInstanceValuesTable)
